import React, { ReactNode } from 'react';
import { Box, Tab, Tabs, FormControlLabel, Switch, Typography, Tooltip, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  label?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
}

interface TabConfig {
  label: string;
  disabled?: boolean;
  content: ReactNode;
}

interface TabManagerProps {
  tabs: TabConfig[];
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  showOptimizedToggle?: boolean;
  showOptimized?: boolean;
  onToggleOptimized?: (checked: boolean) => void;
  optimizing?: boolean;
}

const TabManager: React.FC<TabManagerProps> = ({
  tabs,
  activeTab,
  onTabChange,
  showOptimizedToggle = false,
  showOptimized = false,
  onToggleOptimized,
  optimizing = false,
}) => {
  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 1, 
        borderColor: 'divider' 
      }}>
        <Tabs value={activeTab} onChange={onTabChange} aria-label="application tabs">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} disabled={tab.disabled} />
          ))}
        </Tabs>
        
        {showOptimizedToggle && onToggleOptimized && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: showOptimized ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}>
            <Tooltip title="Optimization reduces scheduling conflicts by selecting the most efficient combination of non-overlapping service opportunities" arrow>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOptimized}
                    onChange={(e) => onToggleOptimized(e.target.checked)}
                    color="primary"
                    disabled={optimizing}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AutoAwesomeIcon fontSize="small" color={showOptimized ? "primary" : "action"} />
                    <Typography variant="body2" color={showOptimized ? "primary" : "text.secondary"}>
                      {optimizing ? "Optimizing..." : "Show Optimized Schedule"}
                    </Typography>
                  </Box>
                }
              />
            </Tooltip>
          </Box>
        )}
      </Box>
      
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={activeTab} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </>
  );
};

export default TabManager;
